import React, { useCallback, useEffect, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Moderation queue for member testimonials.
 *
 * Nothing reaches the public site without an explicit Approve here, and the
 * recorded consent wording + timestamp are shown alongside each quote so the
 * reviewer can see exactly what the member agreed to before publishing it.
 * Submissions from automated test accounts are flagged and are excluded from
 * the public endpoint even if approved.
 */
const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Published" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminTestimonials() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/testimonials?status=${status}`);
      setItems(data?.testimonials || []);
      setCounts(data?.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/testimonials/${id}/review`, { action });
      toast.success(action === "approve" ? "Published." : "Rejected.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <section data-testid="admin-testimonials" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatus(f.key)}
            data-testid={`admin-testimonials-filter-${f.key}`}
            className={`rounded-xl px-4 py-2 text-sm font-semibold border transition-colors ${
              status === f.key
                ? "bg-[#1F6F78] text-white border-[#1F6F78]"
                : "bg-white text-[#4B5A60] border-[#E3DED4] hover:bg-[#F2EFE8]"
            }`}
          >
            {f.label} ({counts[f.key] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[#4B5A60]">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading
        </p>
      ) : !items.length ? (
        <p data-testid="admin-testimonials-empty" className="sm-card p-6 text-sm text-[#4B5A60]">
          Nothing here. No testimonials are published on the public site, and none are invented -
          the homepage shows the founder note and product walkthrough instead.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((t) => (
            <li
              key={t.id}
              data-testid={`admin-testimonial-${t.id}`}
              className="sm-card p-5 bg-white"
            >
              <p className="text-[#1F2A2E] leading-7">“{t.quote}”</p>
              <dl className="mt-3 grid gap-1 text-xs text-[#4B5A60] sm:grid-cols-2">
                <div>
                  <dt className="inline font-semibold">Will appear as: </dt>
                  <dd className="inline">{t.public_label}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Photo: </dt>
                  <dd className="inline">{t.show_photo && t.has_photo ? "yes" : "no"}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Consent recorded: </dt>
                  <dd className="inline">{t.consent_at || "-"}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Test account: </dt>
                  <dd className="inline">{t.is_test_account ? "yes (never published)" : "no"}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs text-[#7A868B] leading-5">{t.consent_wording}</p>
              {status !== "approved" || t.status !== "approved" ? null : null}
              <div className="mt-4 flex flex-wrap gap-2">
                {t.status !== "approved" && (
                  <Button
                    disabled={busy === t.id}
                    onClick={() => review(t.id, "approve")}
                    data-testid={`admin-testimonial-approve-${t.id}`}
                    className="h-10 px-5 bg-[#1F6F78] hover:bg-[#185B62] text-white"
                  >
                    <Check className="w-4 h-4 mr-1.5" aria-hidden="true" /> Approve &amp; publish
                  </Button>
                )}
                {t.status !== "rejected" && (
                  <Button
                    disabled={busy === t.id}
                    variant="outline"
                    onClick={() => review(t.id, "reject")}
                    data-testid={`admin-testimonial-reject-${t.id}`}
                    className="h-10 px-5 bg-white border-[#E3DED4] text-[#B42318] hover:bg-[#FBE6E4]"
                  >
                    <X className="w-4 h-4 mr-1.5" aria-hidden="true" /> Reject
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
